import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { eventBookings } from "../../../../services/eventBookingService";
import Toster from "../../../common/Toster";
import Pagination from "../../../common/Pagination";
import Dialog from "../../../dialog/Dialog";
import { AdminPageCard, AdminPageCardToolbar } from "../../../common/AdminPageLayout";
import { getTableEmptyProps } from "../../../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../../../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import { getPricingFeeFields } from "../BookingPricingFees";
import {
  formatDate,
  formatTime,
  getPaymentStatus,
  getPaymentStatusColor,
  selectedRowClass,
} from "../bookingDetailHelpers";
import {
  BookingDetailHeader,
  BookingDetailSection,
  BookingDetailFields,
  BookingDetailTable,
  BookingDetailMetaChip,
  BookingDetailBadge,
  BookingDetailUserCard,
  BookingDetailNav,
  BookingPaymentProgress,
  CopyableText,
} from "../BookingDetailUi";

const SECTION_IDS = {
  overview: "event-booking-overview",
  payment: "event-booking-payment",
  customer: "event-booking-customer",
};

const NAV_ITEMS = [
  { id: SECTION_IDS.overview, label: "Overview", icon: "fi fi-rr-apps" },
  { id: SECTION_IDS.payment, label: "Payments", icon: "fi fi-rr-credit-card" },
  { id: SECTION_IDS.customer, label: "Customer", icon: "fi fi-rr-user" },
];

const EventBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_IDS.overview);
  const [copyToast, setCopyToast] = useState(null);

  // React Query hooks
  const { data: eventBookingsData } = useQuery({
    queryKey: ["eventBookings", currentPage, searchQuery],
    queryFn: () => eventBookings(currentPage, searchQuery),
  });

  const eventBookingsList = eventBookingsData?.data?.data || [];
  const pagination = {
    currentPage: eventBookingsData?.data?.current_page || 1,
    lastPage: eventBookingsData?.data?.last_page || 1,
    perPage: eventBookingsData?.data?.per_page || 10,
    total: eventBookingsData?.data?.total || 0,
    from: eventBookingsData?.data?.from || 0,
    to: eventBookingsData?.data?.to || 0,
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setActiveSection(SECTION_IDS.overview);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
    setActiveSection(SECTION_IDS.overview);
  };

  const handleCopied = useCallback((label, type = "success") => {
    setCopyToast({ message: `${label} copied to clipboard`, type });
    setTimeout(() => setCopyToast(null), 2500);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const selectedBooking = eventBookingsList.find(
    (booking) => booking.id === selectedBookingId
  );

  return (
    <>
      {copyToast && (
        <Toster
          message={copyToast.message}
          type={copyToast.type}
          duration={2500}
          onClose={() => setCopyToast(null)}
        />
      )}

      {/* Error Toster */}
      {showError && (
        <Toster
          message="An error occurred"
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={!!searchQuery}
            onReset={() => setSearchQuery("")}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search event bookings..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable className="shadow-none rounded-none">
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Event</AdminTableTh>
            <AdminTableTh>Event Date</AdminTableTh>
            <AdminTableTh>Total Amount</AdminTableTh>
            <AdminTableTh>Paid Amount</AdminTableTh>
            <AdminTableTh>Balance</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {eventBookingsList.length > 0 ? (
              eventBookingsList.map((booking, index) => (
                <AdminTableRow
                  key={booking.id}
                  className={selectedRowClass(
                    selectedBookingId === booking.id && showDetailsDialog
                  )}
                >
                  <AdminTableCell>{index + 1}</AdminTableCell>
                  <AdminTableCell>{booking.event?.name || "Event Name"}</AdminTableCell>
                  <AdminTableCell>
                    {formatDate(booking.event_date || booking.created_at)}
                  </AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_amount)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_paid)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.balance)}</AdminTableCell>
                  <AdminTableCell>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ring-1 ring-inset ${getPaymentStatusColor(
                        getPaymentStatus(booking)
                      )}`}
                    >
                      {getPaymentStatus(booking)}
                    </span>
                  </AdminTableCell>
                  <TableActionsCell>
                    <ViewDetailsButton
                      onClick={() => handleViewDetails(booking)}
                      ariaLabel={`View details for booking ${booking.id}`}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={8}
                {...getTableEmptyProps("eventBookings", {
                  hasActiveFilters: !!searchQuery,
                  onClearFilters: () => setSearchQuery(""),
                })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Event Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-full"
          variant="detail"
          showCloseButton={false}
        >
          <div className="space-y-5">
            <BookingDetailNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />

            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking.event?.name || "Event"}
              onCopyBookingId={handleCopied}
              meta={
                <>
                  <BookingDetailMetaChip icon="fi fi-rr-calendar">
                    Event: {formatDate(selectedBooking.event_date || selectedBooking.created_at)}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-marker">
                    {selectedBooking.event?.location || "Location N/A"}
                  </BookingDetailMetaChip>
                  <BookingDetailMetaChip icon="fi fi-rr-ticket">
                    {selectedBooking.ticket_count || 1} ticket(s)
                  </BookingDetailMetaChip>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(selectedBooking.total_amount)}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
              stats={[
                { label: "Paid", value: formatCurrency(selectedBooking.total_paid) },
                { label: "Balance", value: formatCurrency(selectedBooking.balance), highlight: parseFloat(selectedBooking.balance) > 0 },
                { label: "Discount", value: formatCurrency(selectedBooking.discount_amount) },
                { label: "Tickets", value: selectedBooking.ticket_count || 1, icon: "fi fi-rr-ticket" },
              ]}
            />

            <BookingPaymentProgress
              total={selectedBooking.total_amount}
              paid={selectedBooking.total_paid}
              balance={selectedBooking.balance}
              formatCurrency={formatCurrency}
            />

            <div id={SECTION_IDS.overview} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <BookingDetailSection title="Booking Information" icon="fi fi-rr-receipt">
                <BookingDetailFields
                  onCopied={handleCopied}
                  fields={[
                    { label: "Booking ID", value: `#${selectedBooking.id}`, copyable: true, copyValue: selectedBooking.id },
                    { label: "Event Date", value: formatDate(selectedBooking.event_date || selectedBooking.created_at) },
                    { label: "Total Amount", value: formatCurrency(selectedBooking.total_amount), highlight: true },
                    { label: "Discount", value: formatCurrency(selectedBooking.discount_amount) },
                    ...getPricingFeeFields(selectedBooking, formatCurrency),
                    { label: "Paid Amount", value: formatCurrency(selectedBooking.total_paid), highlight: parseFloat(selectedBooking.balance) === 0 },
                    { label: "Balance", value: formatCurrency(selectedBooking.balance) },
                    { label: "Ticket Count", value: selectedBooking.ticket_count || 1 },
                  ]}
                />
              </BookingDetailSection>

              <BookingDetailSection title="Event Details" icon="fi fi-rr-calendar">
                <BookingDetailFields
                  fields={[
                    { label: "Event Name", value: selectedBooking.event?.name || "—", fullWidth: true },
                    { label: "Location", value: selectedBooking.event?.location || "Not specified", fullWidth: true },
                    { label: "Duration", value: selectedBooking.event?.duration || "Not specified" },
                    {
                      label: "Starting Date",
                      value: selectedBooking.event?.starting_date
                        ? formatDate(selectedBooking.event.starting_date)
                        : "Not specified",
                    },
                    {
                      label: "Ending Date",
                      value: selectedBooking.event?.ending_date
                        ? formatDate(selectedBooking.event.ending_date)
                        : "Not specified",
                    },
                  ]}
                />
              </BookingDetailSection>
            </div>

            <BookingDetailSection
              id={SECTION_IDS.payment}
              title="Payment History"
              icon="fi fi-rr-credit-card"
              action={
                (selectedBooking.event_payment || []).length > 0 ? (
                  <span className="text-[10px] font-medium text-gray-400">
                    {(selectedBooking.event_payment || []).length} transaction
                    {(selectedBooking.event_payment || []).length === 1 ? "" : "s"}
                  </span>
                ) : null
              }
            >
              <BookingDetailTable
                columns={["Date", "Amount", "Method", "Status", "Order ID"]}
                emptyTitle="No payments yet"
                emptyMessage="Payment records will appear here once the customer completes a transaction."
                rows={(selectedBooking.event_payment || []).map((payment) => (
                  <tr key={payment.id} className="transition hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm capitalize text-gray-600">
                      {payment.payment_method}
                    </td>
                    <td className="px-4 py-3">
                      <BookingDetailBadge
                        className={
                          payment.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-red-50 text-red-700 ring-red-600/20"
                        }
                      >
                        {payment.status}
                      </BookingDetailBadge>
                    </td>
                    <td className="max-w-[200px] px-4 py-3">
                      <CopyableText
                        value={payment.order_id}
                        label="Order ID"
                        onCopied={handleCopied}
                        mono
                        className="font-mono text-xs text-gray-500"
                      />
                    </td>
                  </tr>
                ))}
              />
            </BookingDetailSection>

            <BookingDetailSection id={SECTION_IDS.customer} title="User Details" icon="fi fi-rr-user">
              <BookingDetailUserCard user={selectedBooking.user} onCopied={handleCopied} />
            </BookingDetailSection>
          </div>
        </Dialog>
      )}

      <div className="mt-4">
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          showInfo={true}
        />
      </div>
    </>
  );
};

export default EventBookings;
